import { FC, useContext, useEffect, useState } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { isLeft } from "fp-ts/lib/Either";
import { useForm } from "react-hook-form";
import di from "../../../../../dependency_injection";
import LocalizationContextType from "../../../../../domain/providers/localization/LocalizationContextType";
import ModalsContextType from "../../../../../domain/providers/modal/ModalsContextType";
import CreateAgentUseCase, {
  CreateAgentUseCaseName,
} from "../../../../../domain/use_cases/qa/create_agent.use_case";
import ButtonComponent from "../../../../components/button/button.component";
import LocalizationContext from "../../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../../utils/KeyWordLocalization";
import Validators from "../../../../utils/Validators";
import QAAgentEntity from "../../../../../domain/entities/qa_agent.entity";
import CreateAgentComponentProps from "./create_agent.component.props";
import RestApi from "../../../../../data/settings/RestApi";
import { Tooltip } from 'react-tooltip';


const CreateAgentComponent: FC<CreateAgentComponentProps> = ({
  onCreated,
  onUpdated,
  onRefresh,
  agent,
}) => {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm();
  const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
  const { addToast } = useContext(ModalsContext) as ModalsContextType;
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const allowedExtensions = ['pdf', 'doc', 'docx', 'txt', 'csv'];
  const restApi = new RestApi();
  const [localAgent, setLocalAgent] = useState<QAAgentEntity | null>(null);
  const [localAgentFiles, setLocalAgentFiles] = useState(agent?.files ?? []);
  const [agentType, setAgentType] = useState<'basic' | 'advanced'>('basic');
  const [hasModifiedType, setHasModifiedType] = useState(false);



  const _handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const validFiles: File[] = [];
    const rejectedFiles: string[] = [];

    newFiles.forEach(file => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension && allowedExtensions.includes(extension)) {
        validFiles.push(file);
      } else {
        rejectedFiles.push(file.name);
      }
    });

    if (rejectedFiles.length > 0) {
      addToast(
        `${i18n("CreateAgentPageNoValid")} ${rejectedFiles.join(', ')}. ${i18n("CreateAgentPageTipes")} ${allowedExtensions.join(', ')}`,
        "error",
        undefined
      );
    }

    const updatedFiles = [...selectedFiles, ...validFiles].filter(
      (file, index, self) =>
        index === self.findIndex(f => f.name === file.name && f.size === file.size)
    );

    if (updatedFiles.length > 10) {
      addToast(i18n("CreateAgentPageMax"), "error", undefined);
      return;
    }

    setSelectedFiles(updatedFiles);
    setValue("files", updatedFiles);
  };


  const _handleRemoveFile = (indexToRemove: number) => {
    const updatedFiles = selectedFiles.filter((_, index) => index !== indexToRemove);
    setSelectedFiles(updatedFiles);
    setValue("files", updatedFiles, { shouldValidate: true });
  };

  const _handleDropFiles = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles: File[] = [];
    const rejectedFiles: string[] = [];

    droppedFiles.forEach(file => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension && allowedExtensions.includes(extension)) {
        validFiles.push(file);
      } else {
        rejectedFiles.push(file.name);
      }
    });

    if (rejectedFiles.length > 0) {
      addToast(
        `${i18n("CreateAgentPageNoValid")} ${rejectedFiles.join(', ')}. ${i18n("CreateAgentPageTipes")} ${allowedExtensions.join(', ')}`,
        "error",
        undefined
      );
    }

    const updatedFiles = [...selectedFiles, ...validFiles].filter(
      (file, index, self) =>
        index === self.findIndex(f => f.name === file.name && f.size === file.size)
    );

    if (updatedFiles.length > 10) {
      addToast(i18n("CreateAgentPageMax"), "error", undefined);
      return;
    }

    setSelectedFiles(updatedFiles);
    setValue("files", updatedFiles);
  };



  const _handleCreateAgent = async (data: any) => {
    setLoading(true);

    const formData = new FormData();
    formData.append("name", data[KeyWordLocalization.CreateAgentPageTitle]);
    formData.append("description", data[KeyWordLocalization.CreateAgentPageDescription]);

    formData.append(
      "target",
      String(data[KeyWordLocalization.CreateAgentPageTarget])
    );
    formData.append(
      "flexibility",
      String(data[KeyWordLocalization.CreateAgentFlexibility])
    );

    formData.append("is_assistant", agentType === "advanced" ? "true" : "false");


    Array.from(formData.entries()).forEach(([key, value]) => {
    });

    const files: File[] = getValues("files") ?? [];
    files.forEach((file) => formData.append("files", file));


    let totalSize = 0;
    selectedFiles.forEach((file) => (totalSize += file.size));
    if (totalSize > 100 * 1024 * 1024) {
      addToast(i18n(KeyWordLocalization.CreateAgentPageMaxSizeExceeded), "error", undefined);
      setLoading(false);
      return;
    }




    const response = await di
      .get<CreateAgentUseCase>(CreateAgentUseCaseName)
      .call(formData);

    if (isLeft(response)) {
      addToast(i18n(KeyWordLocalization.CreateAgentPageCreateError), "error", undefined);
      setLoading(false);
      return;
    }

    addToast(i18n(KeyWordLocalization.CreateAgentPageCreateSuccess), "success", undefined);
    onCreated?.(response.right);
    setLoading(false);
  };


  const _loadInfo = async () => {
    if (agent == null) return;
    setValue(KeyWordLocalization.CreateAgentPageTitle, agent.name);
    setValue(KeyWordLocalization.CreateAgentPageDescription, agent.description);
    setValue(KeyWordLocalization.CreateAgentPageTarget, agent.target);
    setValue(KeyWordLocalization.CreateAgentFlexibility, agent.flexibility);

  };

  const _handleOnEdit = async () => {
    if (!agent || !onUpdated) return;
    setLoading(true);

    const formData = new FormData();
    formData.append("id", agent.id);
    formData.append("name", getValues(KeyWordLocalization.CreateAgentPageTitle));
    formData.append("description", getValues(KeyWordLocalization.CreateAgentPageDescription));
    formData.append("target", String(getValues(KeyWordLocalization.CreateAgentPageTarget)));
    formData.append("flexibility", String(getValues(KeyWordLocalization.CreateAgentFlexibility)));
    formData.append("is_assistant", String(agentType === 'advanced'));

    await onUpdated(formData); // Esto solo actualiza los datos base

    // Ahora subimos los archivos
    if (selectedFiles.length > 0) {
      const fileFormData = new FormData();
      fileFormData.append("assistant_id", agent.assistant_id);
      if (agent.vector_store_id) {
        fileFormData.append("vector_store_id", agent.vector_store_id);
      }
      selectedFiles.forEach((file) => fileFormData.append("files", file));

      try {
        const api = new RestApi();
        await api.post(`/agent/${agent.id}/files`, fileFormData);
        addToast(i18n(KeyWordLocalization.CreateAgentPageFilesUploadSuccess), "success", {});
      } catch (error) {
        addToast(i18n(KeyWordLocalization.CreateAgentPageFilesUploadError), "error", {});
      }
    }

    if (onRefresh) await onRefresh(); // Para actualizar la lista
    setLoading(false);
  };

  const _handleDeleteFileFromAgent = async (file: {
    file_id: string;
    filename: string;
    vector_store_id: string;
  }) => {
    if (!agent || !agent.assistant_id || !agent.vector_store_id) return;

    setLoading(true);

    try {
      const restApi = new RestApi();

      await restApi.remove(
        `/agent/${agent.id}/files/${file.file_id}?assistant_id=${agent.assistant_id}&vector_store_id=${file.vector_store_id}`
      );
      if (onRefresh) await onRefresh();
      setLocalAgentFiles(agent.files);


      addToast(i18n(KeyWordLocalization.CreateAgentPageFileDeleteSuccess), "success", {});

      // ✅ ACTUALIZA el estado local
      const updated = localAgentFiles.filter((f) => f.file_id !== file.file_id);
      setLocalAgentFiles(updated);
    } catch (error) {
      console.error("Error al eliminar archivo:", error);
      addToast(i18n(KeyWordLocalization.CreateAgentPageFileDeleteError), "error", {});
    } finally {
      setLoading(false);
    }
  };


  const _handleDeleteAllAgentFiles = async () => {
    if (!agent || !agent.assistant_id || !agent.vector_store_id) return;

    setLoading(true);

    try {
      const restApi = new RestApi();

      // Recorre cada archivo y llama la API de eliminación
      await Promise.all(
        localAgentFiles.map((file) =>
          restApi.remove(
            `/agent/${agent.id}/files/${file.file_id}?assistant_id=${agent.assistant_id}&vector_store_id=${file.vector_store_id}`
          )
        )
      );

      setLocalAgentFiles([]);
      addToast(i18n(KeyWordLocalization.CreateAgentPageAllFilesDeletedSuccess), "success", {});
    } catch (error) {
      addToast(i18n(KeyWordLocalization.CreateAgentPageAllFilesDeletedError), "error", {});
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    register("files", { required: false });
  }, [register]);


  useEffect(() => {
    const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);


  useEffect(() => {
    if (!agent) return;

    reset({
      [KeyWordLocalization.CreateAgentPageTitle]: agent.name,
      [KeyWordLocalization.CreateAgentPageDescription]: agent.description,
      [KeyWordLocalization.CreateAgentPageTarget]: agent.target,
      [KeyWordLocalization.CreateAgentFlexibility]: agent.flexibility,
      files: [],
    });
    if (agent && agent.is_assistant) {
      setAgentType('advanced');
    } else {
      setAgentType('basic');
    }

    setLocalAgentFiles(agent.files ?? []);
  }, [agent, reset]);




  return (

    <form
      className="row"
      onSubmit={handleSubmit(
        agent == null ? _handleCreateAgent : _handleOnEdit
      )}
    >

      <div
        className={`form-group col-md-6 my-3 ${errors[KeyWordLocalization.CreateAgentPageTitle] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(KeyWordLocalization.CreateAgentPageTitle)}
        </label>
        <input
          type="text"
          disabled={loading}
          className="form-control"
          placeholder={i18n(KeyWordLocalization.CreateAgentPageTitle)}
          {...register(
            KeyWordLocalization.CreateAgentPageTitle,
            Validators({ required: true })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={KeyWordLocalization.CreateAgentPageTitle}
        />
      </div>

      <div
        className={`form-group col-md-3 my-3 ${errors[KeyWordLocalization.CreateAgentPageTarget] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(KeyWordLocalization.CreateAgentPageTarget)}
        </label>
        <input
          type="number"
          step={0.1}
          min={0}
          max={100}
          disabled={loading}
          className="form-control"
          placeholder={i18n(KeyWordLocalization.CreateAgentPageTarget)}
          {...register(
            KeyWordLocalization.CreateAgentPageTarget,
            Validators({ required: true, minValue: 0, maxValue: 100 })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={KeyWordLocalization.CreateAgentPageTarget}
        />
      </div>

      <div
        className={`form-group col-md-3 my-3 ${errors[KeyWordLocalization.CreateAgentFlexibility] && "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(KeyWordLocalization.CreateAgentFlexibility)} (0.0 – 1.0)
        </label>
        <input
          type="number"
          step={0.1}
          min={0.0}
          max={1.0}
          disabled={loading}
          className="form-control"
          placeholder="Ej: 0.5"
          {...register(
            KeyWordLocalization.CreateAgentFlexibility,
            Validators({ required: true, minValue: 0.0, maxValue: 1.0 })
          )}
        />
        <small className="form-text text-muted">
          Value between 0.0 and 1.0 (0.1 increments)
        </small>
        <ErrorMessage
          as="aside"
          errors={errors}
          name={KeyWordLocalization.CreateAgentFlexibility}
        />
      </div>

      <div
        className={`form-group ${errors[KeyWordLocalization.CreateAgentPageDescription] &&
          "error error_group"
          }`}
      >
        <label className="form-label">
          {i18n(KeyWordLocalization.CreateAgentPageDescription)}
        </label>
        <textarea
          disabled={loading}
          className="form-control"
          placeholder={i18n(KeyWordLocalization.CreateAgentPageDescription)}
          {...register(
            KeyWordLocalization.CreateAgentPageDescription,
            Validators({ maxLength: 255 })
          )}
        />
        <ErrorMessage
          as="aside"
          errors={errors}
          name={KeyWordLocalization.CreateAgentPageDescription}
        />
      </div>

      {/* <div
        className={`form-group col-md-12 my-3 ${errors.files && "error error_group"}`}>
        <label className="form-label">Archivos (máx. 10, hasta 100 MB en total)</label>
        <input
          type="file"
          className="form-control"
          disabled={loading || selectedFiles.length >= 10}
          multiple
          accept=".pdf,.doc,.docx,.txt,.csv"
          onChange={_handleFileChange} />
        <small className="form-text text-muted">
          Puedes subir hasta 10 archivos. Tipos permitidos: PDF, DOCX, TXT, CSV.
        </small>
        <ErrorMessage as="aside" errors={errors} name="files" />

        {selectedFiles.length > 0 && (
          <div className="p-2 mt-2 rounded border">
           <strong>📁 Archivos cargados:</strong>
            <ul className="mb-0 mt-1 list-unstyled">
              {selectedFiles.map((file, index) => (
                <li key={index} className="d-flex align-items-center justify-content-between text-sm mb-1">
                  <span className="text-truncate">{file.name}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger btn-icon"
                    onClick={() => _handleRemoveFile(index)}
                    title="Eliminar archivo"
                  >
                    <span className="material-symbols-outlined">delete_forever</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div> */}

      {agent == null && (
        <div className="form-group col-md-6 my-3">
          <label className="form-label">
            {i18n(KeyWordLocalization.CreateAgentPageSelectAgentType)}
          </label>

          <div className="d-flex gap-5 align-items-center" style={{ width: "30rem" }}>
            {/* Básico */}
            <div className="form-check form-switch d-flex align-items-center gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="agentTypeBasic"
                checked={agentType === 'basic'}
                onChange={() => {
                  setAgentType('basic');
                  setHasModifiedType(true);
                }}
              />
              <label className="form-check-label mb-0" htmlFor="agentTypeBasic">
                {i18n(KeyWordLocalization.CreateAgentPageBasicAgent)}
              </label>
              <img
                src="/assets/info.png"
                alt="info"
                height={24}
                data-tooltip-id="tooltip-basic"
                data-tooltip-content={i18n(KeyWordLocalization.CreateAgentPageBasicTooltip)}
                style={{ cursor: "pointer" }}
              />
              <Tooltip id="tooltip-basic" />
            </div>

            {/* Avanzado */}
            <div className="form-check form-switch d-flex align-items-center gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="agentTypeAdvanced"
                checked={agentType === 'advanced'}
                onChange={() => {
                  setAgentType('advanced');
                  setHasModifiedType(true);
                }}
              />
              <label className="form-check-label mb-0" htmlFor="agentTypeAdvanced">
                {i18n(KeyWordLocalization.CreateAgentPageAdvancedAgent)}
              </label>
              <img
                src="/assets/info.png"
                alt="info"
                height={24}
                data-tooltip-id="tooltip-advanced"
                data-tooltip-content={i18n(KeyWordLocalization.CreateAgentPageAdvancedTooltip)}
                style={{ cursor: "pointer" }}
              />
              <Tooltip id="tooltip-advanced" />
            </div>
          </div>
        </div>
      )}

      {agentType === 'advanced' && (
        <div
          className={`form-group col-md-12 my-3 ${errors.files && "error error_group"}`}
        >
          <label className="form-label">
            {i18n(KeyWordLocalization.CreateAgentPageFiles)}</label>

          {/* Zona de Drop */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}

            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);

              const droppedFiles = Array.from(e.dataTransfer.files);
              const validFiles: File[] = [];
              const rejectedFiles: string[] = [];

              droppedFiles.forEach(file => {
                const extension = file.name.split('.').pop()?.toLowerCase();
                if (extension && allowedExtensions.includes(extension)) {
                  validFiles.push(file);
                } else {
                  rejectedFiles.push(file.name);
                }
              });

              if (rejectedFiles.length > 0) {
                addToast(
                  `${i18n("CreateAgentPageNoValid")} ${rejectedFiles.join(', ')}. ${i18n("CreateAgentPageTipes")} ${allowedExtensions.join(', ')}`,
                  "error",
                  undefined
                );
              }

              const updatedFiles = [...selectedFiles, ...validFiles].filter(
                (file, index, self) =>
                  index === self.findIndex(f => f.name === file.name && f.size === file.size)
              );

              if (updatedFiles.length > 10) {
                addToast(i18n("CreateAgentPageMax"), "error", undefined);
                return;
              }

              setSelectedFiles(updatedFiles);
              setValue("files", updatedFiles, { shouldValidate: true });
            }}

            className={`border p-3 text-center rounded cursor-pointer ${isDragging ? "bg-secondary text-white" : "bg-dark text-light"}`}
            onClick={() => document.getElementById("fileInput")?.click()}
            style={{ borderStyle: "dashed" }}
          >
            <span>{i18n(KeyWordLocalization.CreateAgentPageDragDrop)}</span>
            <br />
            <small className="form-text text-muted">
              {i18n(KeyWordLocalization.CreateAgentPageFormats)}
            </small>
          </div>

          <input
            id="fileInput"
            type="file"
            className="d-none"
            multiple
            accept=".pdf,.doc,.docx,.txt,.csv"
            disabled={loading}
            onChange={(e) => {
              const newFiles = Array.from(e.target.files || []);
              const updatedFiles = [...selectedFiles, ...newFiles].filter(
                (file, index, self) =>
                  index === self.findIndex(f => f.name === file.name && f.size === file.size)
              );

              if (updatedFiles.length > 10) {
                addToast(i18n("CreateAgentPageMax"), "error", undefined);
                return;
              }

              setSelectedFiles(updatedFiles);
              setValue("files", updatedFiles, { shouldValidate: true });
            }}
          />


          <ErrorMessage as="aside" errors={errors} name="files" />

          {/* Lista de archivos cargados */}
          {selectedFiles.length > 0 && (
            <div
              className="p-2 mt-2 rounded border"
              onDragOver={(e) => e.stopPropagation()} // evita conflicto con zona de drop
            >
              <strong>📁</strong>
              <strong>{i18n(KeyWordLocalization.CreateAgentPageLoad)}</strong>

              <ul className="mb-0 mt-1 list-unstyled">
                {selectedFiles.map((file, index) => (
                  <li
                    key={index}
                    className="d-flex align-items-center justify-content-between text-sm mb-1"
                  >
                    <span className="text-truncate">{file.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger btn-icon"
                      onClick={() => {
                        const updated = selectedFiles.filter((_, i) => i !== index);
                        setSelectedFiles(updated);
                        setValue("files", updated, { shouldValidate: true });
                      }}
                      title="Eliminar archivo"
                    >
                      <span className="material-symbols-outlined">delete_forever</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {localAgentFiles.length > 0 && (
            <div className="p-2 mt-2 rounded border">
              <div className="d-flex justify-content-between align-items-center">
                <strong>{i18n(KeyWordLocalization.CreateAgentPageCurrentFiles)}</strong>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={_handleDeleteAllAgentFiles}
                  disabled={loading}
                  style={{
                    display: "flex",
                    marginBottom: "1rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <span className="material-symbols-outlined">delete_sweep</span>
                  {i18n(KeyWordLocalization.CreateAgentPageDeleteAll)}
                </button>
              </div>

              <ul className="mb-0 mt-1 list-unstyled">
                {localAgentFiles.map((file, index) => (
                  <li
                    key={index}
                    className="d-flex align-items-center justify-content-between text-sm mb-1"
                  >
                    <span className="text-truncate">
                      {typeof file === 'string' ? file : file.filename}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger btn-icon"
                      title="Eliminar archivo del agente"
                      onClick={() => _handleDeleteFileFromAgent(file)}
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}


        </div>
      )}


      <div className="w-100 d-flex justify-content-end my-3">
        {agent == null ? (
          <ButtonComponent
            isLoading={loading}
            icon={<span className="material-symbols-outlined">add</span>}
            type="submit"
            design="primaryOutline"
            text={i18n(KeyWordLocalization.CreateAgentPageCreate)}
          />
        ) : (
          <ButtonComponent
            isLoading={loading}
            icon={<span className="material-symbols-outlined">save</span>}
            type="submit"
            design="primaryOutline"
            text={i18n(KeyWordLocalization.CreateAgentPageUpdate)}
          />
        )}
      </div>
    </form>

  );
};

export default CreateAgentComponent;
